import './contact-us.scss';

import { PageProps, graphql } from 'gatsby';

import React from 'react';
import { SEOArguments } from '../classes/seoArguments';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';
import { LocalType } from '../models/localType';
import SEO from '../components/Seo';
import { Button } from '../components/primitives';
import MainLayout from '../components/layout/MainLayout';

export const query = graphql`
    query ContactUsPage {
        customerServiceInfo: allProductsJson(
            filter: { code: { eq: "century_customer-service" } }
        ) {
            nodes {
                ...contactUsInformationFragment
            }
        }
        technicalSupportInfo: allProductsJson(
            filter: { code: { eq: "century_technical-support" } }
        ) {
            nodes {
                ...contactUsInformationFragment
            }
        }
        metadata: allProductsJson(filter: { code: { eq: "century_contact" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function ContactUs({
    data,
}: PageProps<Queries.ContactUsPageQuery>) {
    const { t } = useTranslation('pages');
    const { currentLocale } = usePageLocalization();

    const customerServiceInfo = data?.customerServiceInfo?.nodes?.[0];
    const technicalSupportInfo = data?.technicalSupportInfo?.nodes?.[0];

    const seoArguments = new SEOArguments(
        data.metadata,
        currentLocale as keyof LocalType
    );

    const customerServiceTitle = customerServiceInfo?.title?.[currentLocale];
    const technicalSupportTitle = technicalSupportInfo?.title?.[currentLocale];

    const customerServicePhone =
        customerServiceInfo?.attributes?.texte_1?.values?.[0]?.[currentLocale];
    const technicalSupportPhone =
        technicalSupportInfo?.attributes?.texte_1?.values?.[0]?.[currentLocale];

    const customerServiceEmail =
        customerServiceInfo?.attributes?.link?.values?.[0]?.[currentLocale];
    const technicalSupportEmail =
        technicalSupportInfo?.attributes?.link?.values?.[0]?.[currentLocale];

    const customerServiceOpeningDays =
        customerServiceInfo?.attributes?.texte_2?.values?.[0]?.[currentLocale];
    const technicalSupportOpeningDays =
        technicalSupportInfo?.attributes?.texte_2?.values?.[0]?.[currentLocale];

    const customerServiceOpeningHours =
        customerServiceInfo?.attributes?.texte_3?.values?.[0]?.[currentLocale];
    const technicalSupportOpeningHours =
        technicalSupportInfo?.attributes?.texte_3?.values?.[0]?.[currentLocale];

    return (
        <MainLayout>
            <SEO
                title={seoArguments.title}
                description={seoArguments.description}
            />
            <div className='container contactUs'>
                <h1>{t('contact-title')}</h1>
                <div className='contactWrapper'>
                    <div className='contactCategory'>
                        <span className='iconfont iconfont-ecouteur'></span>
                        <div className='information'>
                            <h3>{customerServiceTitle?.toUpperCase()}</h3>
                            <p>
                                <strong>{customerServicePhone}</strong>
                                <br />
                                {customerServiceOpeningDays}
                                {' - '}
                                {customerServiceOpeningHours}
                                <br />
                                <a href={`mailto:${customerServiceEmail}`}>
                                    {customerServiceEmail}
                                </a>
                            </p>
                        </div>
                        <Button
                            link='/contact-us/customer-service/'
                            className='contactBtn'
                            label={t('customer-service-btn')}
                        ></Button>
                    </div>
                    <div className='contactCategory'>
                        <span className='iconfont iconfont-soutien'></span>
                        <div className='information'>
                            <h3>{technicalSupportTitle?.toUpperCase()}</h3>
                            <p>
                                <strong>{technicalSupportPhone}</strong>
                                <br />
                                {technicalSupportOpeningDays}
                                {' - '}
                                {technicalSupportOpeningHours}
                                <br />
                                <a href={`mailto:${technicalSupportEmail}`}>
                                    {technicalSupportEmail}
                                </a>
                            </p>
                        </div>
                        <Button
                            link='/contact-us/technical-support/'
                            className='contactBtn'
                            label={t('technical-support-btn')}
                        ></Button>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}
